//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import {
  PoolAccountCTO,
  PoolBaseAccountCTO,
  PoolDynamicAccountCTO,
  PoolDynamicOverviewCTO,
  PoolFlexAccountCTO,
} from '@shared/sharedTypes'
import React, { useEffect } from 'react'

import { EthAddr } from '@shared/SchemaTypes'

import { useOtChainState, useOtDispatch, useOtSelector } from '@react/redux/store'
import { useParams } from 'react-router-dom'

import { PoolTypeEnum } from '@shared/Enums'

import { fetchPoolAccountCTO } from '@react/fetchers/PoolFetchers'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { poolActions } from '@react/redux/poolSlice'
import { logOT } from '@shared/loggingShared'

// Import the dedicated pool type components
import TermPoolHoldings from '@react/lender/components/TermPoolHoldings'
import FlexPoolHoldings from '@react/lender/components/FlexPoolHoldings'
import DynamicPoolHoldings from '@react/lender/components/DynamicPoolHoldings'
import PoolDetailsSkeleton from '@react/components/skeletons/PoolDetailsSkeleton'

export default function LenderPoolHoldings(props: {
  poolAddr?: EthAddr
  accountAddr?: EthAddr
  blockNumberSnapshot?: number
}): React.ReactElement {
  const poolAddr = (props.poolAddr ? props.poolAddr : useParams().poolAddr) as EthAddr

  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    userConnection: { accountAddr: accountAddrLoggedIn },
  } = useOtChainState()

  const accountAddr = props.accountAddr ?? accountAddrLoggedIn
  const { blockNumberSnapshot } = props
  const { poolAccountCTOs } = useOtSelector((state) => state.poolState)

  const [poolBaseAccountCTOAtBlock, setPoolBaseAccountCTOAtBlock] = React.useState<PoolBaseAccountCTO | undefined>(
    undefined
  )

  const poolBaseOverviewCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO])
  const dispatch = useOtDispatch()

  useEffect(() => {
    const asyncFunction = async () => {
      if (poolAddr && accountAddr && serverApiBaseUrl && restAPIKey) {
        if (blockNumberSnapshot) {
          const _poolBaseAccountCTO = await fetchPoolAccountCTO(
            serverApiBaseUrl,
            restAPIKey,
            poolAddr,
            accountAddr,
            blockNumberSnapshot
          )
          setPoolBaseAccountCTOAtBlock(_poolBaseAccountCTO)
        } else {
          const _poolBaseAccountCTO = poolAccountCTOs[poolAddr]?.[accountAddr] as PoolBaseAccountCTO
          if (_poolBaseAccountCTO) {
            setPoolBaseAccountCTOAtBlock(_poolBaseAccountCTO)
          } else {
            const _poolBaseAccountCTO = await fetchPoolAccountCTO(
              serverApiBaseUrl,
              restAPIKey,
              poolAddr,
              accountAddr,
              blockNumberSnapshot
            )
            if (_poolBaseAccountCTO) {
              setPoolBaseAccountCTOAtBlock(_poolBaseAccountCTO)
              dispatch(poolActions.updatePoolAccountCTO(_poolBaseAccountCTO))
            }
          }
        }
      }
    }
    asyncFunction().catch((error) => {
      logOT.error('fetch error', error)
    })
  }, [poolAddr, accountAddr, blockNumberSnapshot, poolBaseOverviewCTO, poolAccountCTOs])

  if (!poolBaseAccountCTOAtBlock || !poolBaseOverviewCTO || !poolAddr || !accountAddr) {
    return <PoolDetailsSkeleton />
  }

  const poolType = poolBaseAccountCTOAtBlock.poolType

  // Render the appropriate component based on pool type
  if (poolType === PoolTypeEnum.Term) {
    return (
      <TermPoolHoldings
        poolAccountCTO={poolBaseAccountCTOAtBlock as PoolAccountCTO}
        poolBaseAccountCTO={poolBaseAccountCTOAtBlock}
        poolBaseOverviewCTO={poolBaseOverviewCTO}
        poolAddr={poolAddr}
        accountAddr={accountAddr}
      />
    )
  }

  if (poolType === PoolTypeEnum.FlexibleRate) {
    return (
      <FlexPoolHoldings
        poolFlexAccountCTO={poolBaseAccountCTOAtBlock as PoolFlexAccountCTO}
        poolBaseAccountCTO={poolBaseAccountCTOAtBlock}
        poolBaseOverviewCTO={poolBaseOverviewCTO}
        poolAddr={poolAddr}
        accountAddr={accountAddr}
      />
    )
  }

  if (poolType === PoolTypeEnum.Dynamic || poolType === PoolTypeEnum.Lyt) {
    return (
      <DynamicPoolHoldings
        poolDynamicAccountCTO={poolBaseAccountCTOAtBlock as PoolDynamicAccountCTO}
        poolBaseAccountCTO={poolBaseAccountCTOAtBlock}
        poolDynamicOverviewCTO={poolBaseOverviewCTO as PoolDynamicOverviewCTO}
        poolAddr={poolAddr}
        accountAddr={accountAddr}
      />
    )
  }

  return <></>
}
